// Settings → Book Now line (0393).
//
// When Book Now finds nobody because everyone is busy or nobody is online yet,
// the client may wait in line and be told when a therapist is free. The line is
// NOT in the approved spec yet (01B: "Fully Booked; offer Scheduled Booking"),
// so it ships off and every number is a setting, pending Luigi.

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { Switch } from "@bednbody/ui/components/switch";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { type OperatingSettings, useUpdateOperatingSettings } from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";

interface Draft {
  enabled: boolean;
  waitMinutes: string;
  claimMinutes: string;
}

function draftFrom(settings: OperatingSettings): Draft {
  return {
    enabled: settings.waitlist_enabled,
    waitMinutes: String(settings.waitlist_wait_minutes),
    claimMinutes: String(settings.waitlist_claim_minutes),
  };
}

export function WaitlistCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(settings));

  useEffect(() => {
    setDraft(draftFrom(settings));
  }, [settings]);

  const wait = Number(draft.waitMinutes);
  const claim = Number(draft.claimMinutes);
  // The database refuses outside these (0393); checked here so the reason is
  // readable before the round trip.
  const waitValid = Number.isInteger(wait) && wait >= 5 && wait <= 240;
  const claimValid = Number.isInteger(claim) && claim >= 1 && claim <= 30;

  function save() {
    if (!waitValid || !claimValid) {
      toast.error("Whole minutes: 5–240 for a place in line, 1–30 to confirm.");
      return;
    }
    update.mutate(
      {
        waitlist_enabled: draft.enabled,
        waitlist_wait_minutes: wait,
        waitlist_claim_minutes: claim,
      },
      {
        onSuccess: () => toast.success("Book Now line saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader>
        <CardTitle className="type-title-body">
          <h2>Book Now line</h2>
        </CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          When Book Now finds nobody because everyone is busy or no one is online yet, the client
          can wait in line and is notified when a therapist is free. Nothing is booked or charged
          while they wait. Not in the approved spec yet: off until Luigi agrees.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-foreground">Offer “Wait in line”</p>
            <p className="text-sm text-muted-foreground">
              Turning it off ends every place in line within a minute.
            </p>
          </div>
          <Switch
            aria-label="Offer Wait in line"
            checked={draft.enabled}
            onCheckedChange={(enabled) => setDraft((d) => ({ ...d, enabled }))}
          />
        </div>
        <Field label="How long a place in line lasts (minutes, 5–240)">
          <NumberInput
            value={draft.waitMinutes}
            onChange={(waitMinutes) => setDraft((d) => ({ ...d, waitMinutes }))}
          />
        </Field>
        <Field label="Time to confirm once a therapist is free (minutes, 1–30)">
          <NumberInput
            value={draft.claimMinutes}
            onChange={(claimMinutes) => setDraft((d) => ({ ...d, claimMinutes }))}
          />
        </Field>
      </CardContent>
      <CardFooter>
        <Button onClick={save} disabled={update.isPending || !waitValid || !claimValid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}
